import {
  type RefCallback,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useWindowEvent } from '@elfsight/lib-utils';

interface UseArrowOffsets {
  arrowControlSize: number;
  enabled?: boolean;
  horizontalPadding?: number;
}

export const useArrowOffsets = ({
  horizontalPadding,
  arrowControlSize,
  enabled = true
}: UseArrowOffsets) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const baseArrowOffset = arrowControlSize / 2;

  const withHorizontalPadding = horizontalPadding !== undefined;
  const defaultAdditionalOffsets = useMemo(() => {
    const defaultAdditionalOffset = withHorizontalPadding
      ? Math.max(baseArrowOffset - horizontalPadding, 0)
      : 0;

    return {
      left: defaultAdditionalOffset,
      right: defaultAdditionalOffset
    };
  }, [withHorizontalPadding, horizontalPadding, baseArrowOffset]);
  const [
    { left: leftArrowAdditionalOffset, right: rightArrowAdditionalOffset },
    setArrowsAdditionalOffset
  ] = useState(defaultAdditionalOffsets);

  const handleResize = useCallback(() => {
    if (!enabled || !containerRef.current || withHorizontalPadding) {
      setArrowsAdditionalOffset(defaultAdditionalOffsets);

      return;
    }

    const { left, right } = containerRef.current.getBoundingClientRect();

    const newLeftOffset = Math.max(baseArrowOffset - left, 0);
    const newRightOffset = Math.max(
      right + baseArrowOffset - document.documentElement.clientWidth,
      0
    );

    setArrowsAdditionalOffset((previousArrowOffsets) => {
      const { left: previousLeftOffset, right: previousRightOffset } =
        previousArrowOffsets;
      if (
        newLeftOffset === previousLeftOffset &&
        newRightOffset === previousRightOffset
      ) {
        return previousArrowOffsets;
      }

      return {
        left: newLeftOffset,
        right: newRightOffset
      };
    });
  }, [
    baseArrowOffset,
    withHorizontalPadding,
    defaultAdditionalOffsets,
    enabled
  ]);

  useWindowEvent('resize', handleResize);

  const refCallback: RefCallback<HTMLDivElement> = useCallback(
    (node) => {
      resizeObserverRef.current?.disconnect();
      containerRef.current = node;

      if (!node) {
        return;
      }

      handleResize();
      resizeObserverRef.current = new ResizeObserver(handleResize);
      resizeObserverRef.current.observe(node);
    },
    [handleResize]
  );

  useEffect(() => {
    if (!enabled) {
      resizeObserverRef.current?.disconnect();
    }
  }, [enabled]);

  return {
    leftArrowOffset: enabled ? baseArrowOffset - leftArrowAdditionalOffset : 0,
    rightArrowOffset: enabled
      ? baseArrowOffset - rightArrowAdditionalOffset
      : 0,
    ref: refCallback
  };
};
